import React, { useState, useEffect } from "react";
import {
  AlertTriangle,
  Plus,
  ChevronDown,
  ChevronUp,
  Save,
  Trash2,
  MessageSquare,
} from "lucide-react";
import { useBodyScrollLock } from "../utils/useBodyScrollLock";
import { kmsApi } from "../api/client";
import { fmtNumber, fmtRelative, langLabel } from "../utils/format";
import { useAuth } from "../auth/AuthContext";

const STATUS_OPTIONS = [
  { v: "identified", label: "Identified", cls: "badge-saffron" },
  { v: "acknowledged", label: "Acknowledged", cls: "badge-neutral" },
  { v: "in_progress", label: "In Progress", cls: "badge-saffron" },
  { v: "added_to_kms", label: "Added to KMS", cls: "badge-green" },
  { v: "declined", label: "Declined", cls: "badge-red" },
];

const PRIORITY_OPTIONS = [
  { v: "critical", label: "Critical", cls: "badge-red" },
  { v: "high", label: "High", cls: "badge-saffron" },
  { v: "medium", label: "Medium", cls: "badge-neutral" },
  { v: "low", label: "Low", cls: "badge-outline" },
];

const DOMAIN_OPTIONS = [
  "vaicharik",
  "sangathana",
  "seva",
  "shiksha",
  "samajik_samarasta",
  "rashtriya_suraksha",
  "sanskriti",
  "itihaas",
  "general",
];

const Skeleton = ({
  width = "100%",
  height = 20,
  borderRadius = 4,
  style = {},
}) => (
  <div
    className="skeleton-box"
    style={{
      display: "inline-block",
      width,
      height,
      borderRadius,
      ...style,
    }}
  />
);

export default function KMSGaps() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("kms:write");
  const [gaps, setGaps] = useState([]);
  const [filters, setFilters] = useState({ status: "", priority: "", platform: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [gapToDelete, setGapToDelete] = useState(null);
  useBodyScrollLock(showCreate || !!gapToDelete);

  const sortedGaps = React.useMemo(
    () => [...gaps].sort(
      (a, b) => new Date(b.last_occurred_at) - new Date(a.last_occurred_at)
    ),
    [gaps]
  );

  const load = () => {
    setLoading(true);
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.priority) params.priority = filters.priority;
    if (filters.platform) params.platform = filters.platform;
    kmsApi
      .gaps(params)
      .then(setGaps)
      .catch((e) => setError(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filters]);

  const toggleExpand = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  const updateGap = async (id, patch) => {
    try {
      const updated = await kmsApi.updateGap(id, patch);
      setGaps((gs) => gs.map((g) => (g._id === id ? updated : g)));
    } catch (e) {
      alert(e?.response?.data?.error || "Could not update.");
    }
  };

  const confirmDelete = async () => {
    if (!gapToDelete) return;
    try {
      await kmsApi.deleteGap(gapToDelete);
      setGaps((gs) => gs.filter((g) => g._id !== gapToDelete));
      setGapToDelete(null);
    } catch (e) {
      alert(e?.response?.data?.error || "Could not delete.");
    }
  };

  return (
    <div style={{ width: "100%", paddingBottom: 64 }}>
      <style>{`
        @keyframes skeleton-loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .skeleton-box {
          background: linear-gradient(90deg, var(--ink-100, #f3f4f6) 25%, var(--ink-200, #e5e7eb) 50%, var(--ink-100, #f3f4f6) 75%);
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s infinite linear;
        }
        .custom-select {
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          background: #ffffff;
          border: 1px solid var(--ink-200, #e2e8f0);
          border-radius: 8px;
          padding: 8px 36px 8px 14px;
          font-size: 14px;
          font-weight: 500;
          color: var(--ink-800, #1e293b);
          cursor: pointer;
          outline: none;
          transition: all 0.2s ease;
          width: 100%;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
        }
        .custom-select:hover {
          border-color: var(--ink-300, #cbd5e1);
          background-color: var(--paper-1, #f8fafc);
        }
        .custom-select:focus {
          border-color: var(--saffron, #c26c1b);
          box-shadow: 0 0 0 3px rgba(194, 108, 27, 0.12);
        }
      `}</style>

      {/* Hero Section */}
      <div
        style={{
          position: "relative",
          marginBottom: 32,
          padding: "16px 0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 24,
        }}
      >
        <div
          className="devanagari-watermark"
          style={{
            position: "absolute",
            top: -30,
            right: 20,
            fontSize: 180,
            background:
              "linear-gradient(135deg, rgba(194,108,27,0.08) 0%, rgba(194,108,27,0.01) 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            pointerEvents: "none",
            userSelect: "none",
            fontWeight: 800,
            zIndex: 0,
          }}
        >
          रिक्तता
        </div>

        <div style={{ position: "relative", zIndex: 1, maxWidth: 720 }}>
          <div
            className="t-eyebrow"
            style={{ color: "var(--ink-500)", letterSpacing: "0.05em" }}
          >
            Sangathana Console / KMS Gaps
          </div>
          <h1
            className="t-display"
            style={{
              fontSize: 36,
              marginTop: 8,
              marginBottom: 12,
              color: "var(--ink-900)",
            }}
          >
            What we cannot yet answer.
          </h1>
          <p
            style={{
              color: "var(--ink-600)",
              fontFamily: "var(--serif)",
              fontSize: 16,
              fontStyle: "italic",
              lineHeight: 1.5,
            }}
          >
            Each gap is a request the corpus didn’t hold an answer for —
            clustered, scored, and queued for review.
          </p>
        </div>

        {canWrite && (
          <div style={{ position: "relative", zIndex: 1 }}>
            <button
              className="btn btn-primary"
              onClick={() => setShowCreate(true)}
              style={{
                padding: "10px 20px",
                fontSize: 14,
                boxShadow: "0 4px 12px rgba(194, 108, 27, 0.2)",
              }}
            >
              <Plus size={16} /> Log gap manually
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div
        className="panel"
        style={{
          padding: "20px 24px",
          marginBottom: 24,
          background: "#ffffff",
          borderRadius: 16,
          border: "1px solid var(--ink-100)",
          boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
          display: "flex",
          gap: 24,
          flexWrap: "wrap",
        }}
      >
        <div>
          <label
            className="t-eyebrow"
            style={{
              display: "block",
              marginBottom: 6,
              color: "var(--ink-500)",
              fontSize: 11,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Status
          </label>
          <div style={{ position: "relative", width: 200 }}>
            <select
              className="custom-select"
              value={filters.status}
              onChange={(e) =>
                setFilters((f) => ({ ...f, status: e.target.value }))
              }
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s.v} value={s.v}>
                  {s.label}
                </option>
              ))}
            </select>
            <ChevronDown
              size={16}
              style={{
                position: "absolute",
                right: 12,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--ink-400)",
                pointerEvents: "none",
              }}
            />
          </div>
        </div>
        <div>
          <label
            className="t-eyebrow"
            style={{
              display: "block",
              marginBottom: 6,
              color: "var(--ink-500)",
              fontSize: 11,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Platform
          </label>
          <div style={{ position: "relative", width: 200 }}>
            <select
              className="custom-select"
              value={filters.platform}
              onChange={(e) => setFilters((f) => ({ ...f, platform: e.target.value }))}
            >
              <option value="">All platforms</option>
              <option value="alpha">Alpha</option>
              <option value="main">Main</option>
              <option value="beta">Beta</option>
            </select>
            <ChevronDown
              size={16}
              style={{
                position: "absolute",
                right: 12,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--ink-400)",
                pointerEvents: "none",
              }}
            />
          </div>
        </div>
        <div>
          <label
            className="t-eyebrow"
            style={{
              display: "block",
              marginBottom: 6,
              color: "var(--ink-500)",
              fontSize: 11,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Priority
          </label>
          <div style={{ position: "relative", width: 200 }}>
            <select
              className="custom-select"
              value={filters.priority}
              onChange={(e) =>
                setFilters((f) => ({ ...f, priority: e.target.value }))
              }
            >
              <option value="">All priorities</option>
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p.v} value={p.v}>
                  {p.label}
                </option>
              ))}
            </select>
            <ChevronDown
              size={16}
              style={{
                position: "absolute",
                right: 12,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--ink-400)",
                pointerEvents: "none",
              }}
            />
          </div>
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: "16px 20px",
            borderRadius: 12,
            border: "1px solid var(--red)",
            background: "#fff1f2",
            color: "var(--red)",
            marginBottom: 24,
            display: "flex",
            alignItems: "center",
            gap: 12,
            fontWeight: 500,
          }}
        >
          <AlertTriangle size={20} />
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="panel"
              style={{
                background: "#ffffff",
                borderRadius: 16,
                border: "1px solid var(--ink-100)",
                boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
                padding: "20px 24px",
                display: "grid",
                gridTemplateColumns: "1fr auto auto auto",
                gap: 16,
                alignItems: "center",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <Skeleton
                  width="60%"
                  height={20}
                  style={{ marginBottom: 6, display: "block" }}
                />
                <Skeleton
                  width="40%"
                  height={16}
                  style={{ display: "block" }}
                />
              </div>
              <Skeleton width={60} height={24} borderRadius={12} />
              <Skeleton width={80} height={24} borderRadius={12} />
              <Skeleton width={80} height={32} borderRadius={8} />
            </div>
          ))}
        </div>
      ) : !gaps.length ? (
        <div
          style={{
            padding: "64px 24px",
            textAlign: "center",
            color: "var(--ink-400)",
            fontFamily: "var(--serif)",
            fontStyle: "italic",
            fontSize: 18,
            background: "var(--paper-2)",
            borderRadius: 16,
            border: "1px dashed var(--ink-200)",
          }}
        >
          No gaps in scope. The corpus is holding up.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {sortedGaps.map((g) => {
            const priority = PRIORITY_OPTIONS.find((p) => p.v === g.priority);
            const status = STATUS_OPTIONS.find((s) => s.v === g.status);
            const isOpen = expanded[g._id];
            return (
              <div
                key={g._id}
                className="panel"
                style={{
                  background: "#ffffff",
                  borderRadius: 16,
                  border: isOpen
                    ? "1px solid var(--saffron-light)"
                    : "1px solid var(--ink-100)",
                  boxShadow: isOpen
                    ? "0 12px 32px -8px rgba(194, 108, 27, 0.15)"
                    : "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
                  overflow: "hidden",
                }}
              >
                {/* Header Row */}
                <div
                  style={{
                    padding: "20px 24px",
                    display: "grid",
                    gridTemplateColumns: "1fr auto auto auto auto",
                    gap: 16,
                    alignItems: "center",
                    background: isOpen
                      ? "rgba(251, 246, 238, 0.3)"
                      : "transparent",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 16,
                        color: "var(--ink-900)",
                        marginBottom: 6,
                        lineHeight: 1.3,
                      }}
                    >
                      {g.topic}
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: "var(--ink-500)",
                        display: "flex",
                        gap: 12,
                        alignItems: "center",
                        fontWeight: 500,
                      }}
                    >
                      {/* <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <MessageSquare size={13} />
                        {fmtNumber(g.occurrence_count)} occurrence
                        {g.occurrence_count === 1 ? "" : "s"}
                      </span>
                      <span style={{ color: "var(--ink-300)" }}>|</span> */}
                      {/* <span>Last seen {fmtRelative(g.last_occurred_at)}</span> */}
                      {g.proposed_domain && (
                        <>
                          <span style={{ color: "var(--ink-300)" }}>|</span>
                          <span style={{ color: "var(--saffron-deep)" }}>
                            Domain: {g.proposed_domain}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  {priority && (
                    <span
                      className={`badge ${priority.cls}`}
                      style={{ padding: "4px 10px", fontSize: 12 }}
                    >
                      {priority.label}
                    </span>
                  )}
                  {status && (
                    <span
                      className={`badge ${status.cls}`}
                      style={{ padding: "4px 10px", fontSize: 12 }}
                    >
                      {status.label}
                    </span>
                  )}
                  <button
                    className="btn"
                    style={{
                      padding: "8px 12px",
                      fontSize: 13,
                      background: "#ffffff",
                      borderRadius: 9999,
                      width: 96,
                      justifyContent: "center",
                    }}
                    onClick={() => toggleExpand(g._id)}
                  >
                    {isOpen ? (
                      <>
                        <ChevronUp size={14} /> Hide
                      </>
                    ) : (
                      <>
                        <ChevronDown size={14} /> Expand
                      </>
                    )}
                  </button>
                  {canWrite && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setGapToDelete(g._id);
                      }}
                      className="btn btn-ghost"
                      style={{
                        padding: "8px",
                        color: "var(--red)",
                        borderRadius: 8,
                      }}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>

                {/* Expanded Body */}
                {isOpen && (
                  <div
                    style={{
                      padding: "24px",
                      background: "var(--paper-2)",
                      borderTop: "1px solid var(--ink-100)",
                    }}
                  >
                    {g.description && (
                      <div
                        style={{
                          fontFamily: "var(--serif)",
                          fontStyle: "italic",
                          fontSize: 16,
                          lineHeight: 1.6,
                          color: "var(--ink-800)",
                          marginBottom: 24,
                          padding: "16px 20px",
                          background: "#ffffff",
                          borderRadius: 12,
                          borderLeft: "4px solid var(--saffron-light)",
                        }}
                      >
                        {g.description}
                      </div>
                    )}

                    {/* {g.sample_queries?.length > 0 && (
                      <div style={{ marginBottom: 24 }}>
                        <div
                          className="t-eyebrow"
                          style={{ marginBottom: 12, color: "var(--ink-500)" }}
                        >
                          Sample queries
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fill, minmax(300px, 1fr))",
                            gap: 12,
                          }}
                        >
                          {[...g.sample_queries]
                            .sort(
                              (a, b) =>
                                new Date(b.occurred_at) - new Date(a.occurred_at)
                            )
                            .slice(0, 6)
                            .map((q, i) => {
                              const isDevanagari =
                                q.language?.startsWith("hi") ||
                                q.language?.startsWith("mr");
                              return (
                                <div
                                  key={i}
                                  style={{
                                    padding: "14px 18px",
                                    background: "#ffffff",
                                    border: "1px solid var(--ink-200)",
                                    borderRadius: 12,
                                    boxShadow: "0 2px 8px -2px rgba(0,0,0,0.02)",
                                  }}
                                >
                                  <div
                                    style={{
                                      color: "var(--ink-400)",
                                      fontSize: 11,
                                      marginBottom: 8,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.05em",
                                      fontWeight: 600,
                                      display: "flex",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <span>{langLabel(q.language)}</span>
                                    <span>{fmtRelative(q.occurred_at)}</span>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: isDevanagari ? 16 : 14,
                                      lineHeight: 1.6,
                                      fontFamily: isDevanagari
                                        ? "var(--devanagari)"
                                        : "var(--sans)",
                                      color: "var(--ink-900)",
                                    }}
                                  >
                                    “{q.query}”
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )} */}

                    {canWrite && (
                      <div
                        style={{
                          display: "flex",
                          gap: 16,
                          alignItems: "flex-end",
                          flexWrap: "wrap",
                          paddingTop: 24,
                          borderTop: "1px dashed var(--ink-200)",
                        }}
                      >
                        <div>
                          <label
                            className="t-eyebrow"
                            style={{
                              display: "block",
                              marginBottom: 6,
                              color: "var(--ink-500)",
                              fontSize: 11,
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                            }}
                          >
                            Update Status
                          </label>
                          <div style={{ position: "relative", width: 180 }}>
                            <select
                              className="custom-select"
                              defaultValue={g.status}
                              onChange={(e) =>
                                updateGap(g._id, { status: e.target.value })
                              }
                              style={{ fontSize: 13 }}
                            >
                              {STATUS_OPTIONS.map((s) => (
                                <option key={s.v} value={s.v}>
                                  {s.label}
                                </option>
                              ))}
                            </select>
                            <ChevronDown
                              size={16}
                              style={{
                                position: "absolute",
                                right: 12,
                                top: "50%",
                                transform: "translateY(-50%)",
                                color: "var(--ink-400)",
                                pointerEvents: "none",
                              }}
                            />
                          </div>
                        </div>
                        <div>
                          <label
                            className="t-eyebrow"
                            style={{
                              display: "block",
                              marginBottom: 6,
                              color: "var(--ink-500)",
                              fontSize: 11,
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                            }}
                          >
                            Update Priority
                          </label>
                          <div style={{ position: "relative", width: 150 }}>
                            <select
                              className="custom-select"
                              defaultValue={g.priority}
                              onChange={(e) =>
                                updateGap(g._id, { priority: e.target.value })
                              }
                              style={{ fontSize: 13 }}
                            >
                              {PRIORITY_OPTIONS.map((p) => (
                                <option key={p.v} value={p.v}>
                                  {p.label}
                                </option>
                              ))}
                            </select>
                            <ChevronDown
                              size={16}
                              style={{
                                position: "absolute",
                                right: 12,
                                top: "50%",
                                transform: "translateY(-50%)",
                                color: "var(--ink-400)",
                                pointerEvents: "none",
                              }}
                            />
                          </div>
                        </div>
                        <div style={{ flex: 1, minWidth: 280 }}>
                          <label
                            className="t-eyebrow"
                            style={{
                              display: "block",
                              marginBottom: 6,
                              color: "var(--ink-500)",
                              fontSize: 11,
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                            }}
                          >
                            Resolution notes
                          </label>
                          <input
                            className="input"
                            defaultValue={g.resolution_notes || ""}
                            onBlur={(e) => {
                              if (
                                e.target.value !== (g.resolution_notes || "")
                              ) {
                                updateGap(g._id, {
                                  resolution_notes: e.target.value,
                                });
                              }
                            }}
                            style={{
                              padding: "9px 14px",
                              fontSize: 14,
                              background: "#ffffff",
                              borderRadius: 8,
                            }}
                            placeholder="Add notes on disposition…"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <CreateGapModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            load();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {gapToDelete && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(17, 24, 39, 0.4)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 24,
          }}
          onClick={() => setGapToDelete(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#ffffff",
              borderRadius: 24,
              padding: "32px 32px 24px 32px",
              width: "100%",
              maxWidth: 420,
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              border: "1px solid var(--ink-100)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: "#fff1f2",
                color: "#e11d48",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 20,
                boxShadow: "0 0 0 8px rgba(225, 29, 72, 0.05)",
              }}
            >
              <Trash2 size={28} strokeWidth={2} />
            </div>

            <h3
              style={{
                margin: "0 0 10px 0",
                color: "var(--ink-900)",
                fontSize: 22,
                fontWeight: 600,
                letterSpacing: "-0.01em",
              }}
            >
              Delete gap?
            </h3>

            <p
              style={{
                margin: "0 0 32px 0",
                color: "var(--ink-500)",
                fontSize: 15,
                lineHeight: 1.5,
                maxWidth: "90%",
              }}
            >
              Are you sure you want to delete this gap entry? This action cannot
              be undone.
            </p>

            <div style={{ display: "flex", gap: 12, width: "100%" }}>
              <button
                onClick={() => setGapToDelete(null)}
                style={{
                  flex: 1,
                  padding: "12px 0",
                  borderRadius: 12,
                  border: "1px solid var(--ink-200)",
                  background: "#ffffff",
                  color: "var(--ink-700)",
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "var(--paper-2)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "#ffffff")
                }
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  flex: 1,
                  padding: "12px 0",
                  borderRadius: 12,
                  border: "none",
                  background: "#e11d48",
                  color: "#ffffff",
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s",
                  boxShadow: "0 4px 12px rgba(225, 29, 72, 0.2)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#be123c";
                  e.currentTarget.style.boxShadow =
                    "0 6px 16px rgba(225, 29, 72, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#e11d48";
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(225, 29, 72, 0.2)";
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateGapModal({ onClose, onCreated }) {
  const [topic, setTopic] = useState("");
  const [description, setDescription] = useState("");
  const [proposedDomain, setProposedDomain] = useState("general");
  const [priority, setPriority] = useState("medium");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!topic.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await kmsApi.createGap({
        topic: topic.trim(),
        description: description.trim() || undefined,
        proposed_domain: proposedDomain,
        priority,
      });
      onCreated();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(44, 26, 14, 0.5)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50,
        padding: 20,
      }}
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="panel"
        style={{
          width: 540,
          maxWidth: "100%",
          padding: "32px 40px",
          borderRadius: 20,
          background: "#ffffff",
          boxShadow: "0 24px 48px -12px rgba(0,0,0,0.2)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 24,
          }}
        >
          <div>
            <div className="t-eyebrow" style={{ color: "var(--ink-500)" }}>
              Manual entry
            </div>
            <div
              className="t-display"
              style={{ fontSize: 28, marginTop: 4, color: "var(--ink-900)" }}
            >
              Log a KMS gap
            </div>
          </div>
        </div>

        <label
          className="t-eyebrow"
          style={{ display: "block", marginBottom: 6, color: "var(--ink-500)" }}
        >
          Topic *
        </label>
        <input
          className="input"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          required
          style={{
            marginBottom: 20,
            fontSize: 14,
            padding: "10px 14px",
            background: "#ffffff",
          }}
          placeholder="e.g. Local sevashakti unit registration process"
        />

        <label
          className="t-eyebrow"
          style={{ display: "block", marginBottom: 6, color: "var(--ink-500)" }}
        >
          Description
        </label>
        <textarea
          className="textarea"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          style={{
            marginBottom: 20,
            minHeight: 100,
            fontSize: 14,
            padding: "10px 14px",
            background: "#ffffff",
          }}
          placeholder="Describe what exactly is missing from the corpus..."
        />

        <div
          style={{
            display: "grid",
            gap: 16,
            gridTemplateColumns: "1fr 1fr",
            marginBottom: 24,
          }}
        >
          <div>
            <label
              className="t-eyebrow"
              style={{
                display: "block",
                marginBottom: 6,
                color: "var(--ink-500)",
                fontSize: 11,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Proposed domain
            </label>
            <div style={{ position: "relative", width: "100%" }}>
              <select
                className="custom-select"
                value={proposedDomain}
                onChange={(e) => setProposedDomain(e.target.value)}
              >
                {DOMAIN_OPTIONS.map((d) => (
                  <option key={d} value={d}>
                    {d
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (char) => char.toUpperCase())}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={16}
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--ink-400)",
                  pointerEvents: "none",
                }}
              />
            </div>
          </div>
          <div>
            <label
              className="t-eyebrow"
              style={{
                display: "block",
                marginBottom: 6,
                color: "var(--ink-500)",
                fontSize: 11,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Priority
            </label>
            <div style={{ position: "relative", width: "100%" }}>
              <select
                className="custom-select"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p.v} value={p.v}>
                    {p.label}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={16}
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--ink-400)",
                  pointerEvents: "none",
                }}
              />
            </div>
          </div>
        </div>

        {error && (
          <div
            style={{
              padding: "12px 16px",
              background: "var(--red-tint)",
              color: "var(--red)",
              borderRadius: 8,
              fontSize: 13,
              marginBottom: 20,
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <AlertTriangle size={16} />
            {error}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
            paddingTop: 8,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="btn"
            style={{ padding: "10px 20px" }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting || !topic.trim()}
            style={{ padding: "10px 20px" }}
          >
            <Save size={16} /> {submitting ? "Saving…" : "Save gap"}
          </button>
        </div>
      </form>
    </div>
  );
}